/* Nordbelt V10 — Steel / Scroll Cinema interactions. */
(() => {
  "use strict";

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const readJsonConfig = (selector) => {
    const node = document.querySelector(selector);
    if (!node) return {};
    try { return JSON.parse(node.textContent || "{}"); } catch (_error) { return {}; }
  };
  const nbAssetConfig = window.NB10_ASSETS || readJsonConfig("[data-nb-v10-assets-config]");
  const nbProductConfig = window.NB10_PRODUCT || readJsonConfig("[data-nb-v10-product-config]");
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const mobileViewport = window.matchMedia("(max-width: 760px)").matches;
  const compactBuybarViewport = window.matchMedia("(max-width: 820px)").matches;
  const networkConnection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const useLiteVideo = Boolean(networkConnection && (
    networkConnection.saveData === true ||
    networkConnection.effectiveType === "slow-2g" ||
    networkConnection.effectiveType === "2g"
  ));
  const deviceMemory = Number(navigator.deviceMemory || 0);
  const cpuCores = Number(navigator.hardwareConcurrency || 0);
  const slowDisplay = window.matchMedia("(update: slow)").matches;
  const edgeBrowser = /\bEdg\//.test(navigator.userAgent);
  const windowsPlatform = /Windows/i.test(navigator.userAgent) || /Win/i.test(navigator.platform || "");
  // Beperkte hardware bestaat ook op desktop. De eerdere fast-path was per
  // ongeluk alleen mobiel, waardoor managed Windows/Edge-pc's de zware canvas-,
  // video- en glasrendering kregen. Videobronkwaliteit blijft hier bewust los
  // van staan: we verlagen renderkosten, niet de kwaliteit van het bestand.
  const lowPowerDevice = useLiteVideo || slowDisplay ||
    (deviceMemory > 0 && deviceMemory <= 4) ||
    (cpuCores > 0 && cpuCores <= 4) ||
    (edgeBrowser && windowsPlatform && reduceMotion);
  const mobileLite = mobileViewport || lowPowerDevice || Boolean(networkConnection && networkConnection.saveData === true);
  document.documentElement.classList.toggle("nb-low-power", lowPowerDevice);
  document.documentElement.dataset.nbRenderTier = lowPowerDevice ? "efficient" : "full";
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  // De template is volledig geparseerd vóór dit defer-script draait. Bewaar de
  // vaste videolijsten één keer; op deze lange pagina scheelt dat herhaald DOM-
  // zoekwerk bij scroll-, visibility- en page lifecycle-events.
  const allVideos = $$("video");
  const autoplayVideos = allVideos.filter((video) => video.closest("[data-autoplay]"));
  const nonSequenceAutoplayVideos = autoplayVideos.filter((video) => !video.closest("[data-sequence]"));
  allVideos.forEach((video) => {
    if (video.dataset.poster) video.dataset.posterUrl = video.dataset.poster;
  });

  /* ---------- snelle media: pas laden wanneer echt nodig ---------- */
  const loadDeferredImage = (img) => {
    if (!img || !img.dataset.src) return;
    if (img.dataset.srcset) {
      img.srcset = img.dataset.srcset;
      img.removeAttribute("data-srcset");
    }
    img.src = img.dataset.src;
    img.removeAttribute("data-src");
  };
  const decodedImageLoads = new Map();
  const preloadDecodedImage = (url) => {
    if (!url) return Promise.resolve(false);
    if (decodedImageLoads.has(url)) return decodedImageLoads.get(url);
    const ready = new Promise((resolve) => {
      const image = new Image();
      image.onload = () => {
        const decoded = typeof image.decode === "function" ? image.decode() : Promise.resolve();
        decoded.catch(() => {}).then(() => resolve(true));
      };
      image.onerror = () => resolve(false);
      image.src = url;
    });
    decodedImageLoads.set(url, ready);
    ready.then((ok) => { if (!ok) decodedImageLoads.delete(url); });
    return ready;
  };
  const loadVideoPoster = (video) => {
    if (!video || !video.dataset.poster) return;
    video.poster = video.dataset.poster;
    video.removeAttribute("data-poster");
  };
  const ensureVideo = (video) => {
    if (!video || video.dataset.mediaReady === "1") return;
    loadVideoPoster(video);
    const directSrc = useLiteVideo
      ? (video.dataset.srcLite || video.dataset.srcMobile || video.dataset.srcDesktop)
      : (mobileViewport
          ? (video.dataset.srcMobile || video.dataset.srcDesktop)
          : (video.dataset.srcDesktop || video.dataset.srcMobile));
    if (directSrc) {
      // Een directe video.src is betrouwbaarder dan dynamische <source>-nodes
      // in oudere mobiele Safari-versies.
      video.src = directSrc;
    } else {
      $$('source[data-src]', video).forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute("data-src");
      });
    }
    video.dataset.mediaReady = "1";
    video.load();
  };
  const elementVisibleRatio = (element) => {
    if (!element || document.hidden) return 0;
    const r = element.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return 0;
    const visibleWidth = Math.max(0, Math.min(r.right, window.innerWidth) - Math.max(r.left, 0));
    const visibleHeight = Math.max(0, Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0));
    return (visibleWidth * visibleHeight) / (r.width * r.height);
  };
  const visibilityRatios = new WeakMap();
  const pendingPlayRetries = new WeakMap();
  const activePlayAttempts = new WeakMap();
  const videoMayPlay = (video, fresh = false) => {
    const target = video.closest("[data-sequence]") || video;
    const cachedRatio = fresh ? undefined : visibilityRatios.get(target);
    return (typeof cachedRatio === "number" ? cachedRatio : elementVisibleRatio(target)) >= 0.5;
  };
  const clearPendingPlayRetry = (video) => {
    const retry = pendingPlayRetries.get(video);
    if (!retry) return;
    video.removeEventListener("loadeddata", retry);
    video.removeEventListener("canplay", retry);
    pendingPlayRetries.delete(video);
  };
  let mediaSessionUnlocked = false;
  let playRequestId = 0;
  const stopVideo = (video, state = "paused-out-of-view") => {
    if (!video) return;
    clearPendingPlayRetry(video);
    activePlayAttempts.delete(video);
    video.dataset.playRequest = String(++playRequestId);
    video.dataset.playWanted = "0";
    video.dataset.autoplayState = state;
    if (!video.paused) video.pause();
  };
  const safePlay = (video, { forceMuted = false } = {}) => {
    if (!video) return;
    const requestId = String(++playRequestId);
    video.dataset.playRequest = requestId;
    video.dataset.playWanted = "1";
    // iOS Safari beoordeelt autoplay op het moment dat de bron wordt gekoppeld.
    // Zet muted + inline daarom vóór ensureVideo()/load(), niet erna.
    video.playsInline = true;
    video.setAttribute("webkit-playsinline", "");
    video.setAttribute("playsinline", "");
    if (forceMuted) {
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute("muted", "");
    }
    video.preload = "auto";
    ensureVideo(video);

    const start = () => {
      if (video.dataset.playRequest !== requestId || video.dataset.playWanted !== "1") return;
      if (activePlayAttempts.get(video) === requestId) return;
      if (!videoMayPlay(video)) {
        stopVideo(video);
        return;
      }
      video.dataset.autoplayState = "starting";
      const attempt = video.play();
      if (attempt && typeof attempt.then === "function") {
        activePlayAttempts.set(video, requestId);
        attempt
          .then(() => {
            if (activePlayAttempts.get(video) === requestId) activePlayAttempts.delete(video);
            if (video.dataset.playRequest !== requestId) {
              if (video.dataset.playWanted !== "1" && !video.paused) video.pause();
              return;
            }
            clearPendingPlayRetry(video);
            if (!videoMayPlay(video)) {
              stopVideo(video);
            } else {
              video.dataset.autoplayState = "playing";
            }
          })
          .catch(() => {
            if (activePlayAttempts.get(video) === requestId) activePlayAttempts.delete(video);
            if (video.dataset.playRequest === requestId && video.dataset.playWanted === "1") {
              video.dataset.autoplayState = "waiting";
            }
          });
      }
    };

    // Meteen proberen én nogmaals zodra Safari daadwerkelijk frames kan tonen.
    if (video.readyState < 2) {
      clearPendingPlayRetry(video);
      const retry = () => {
        clearPendingPlayRetry(video);
        start();
      };
      pendingPlayRetries.set(video, retry);
      video.addEventListener("loadeddata", retry, { once: true });
      video.addEventListener("canplay", retry, { once: true });
    } else {
      clearPendingPlayRetry(video);
    }
    start();
  };

  let mediaObserver = null;
  if ("IntersectionObserver" in window) {
    mediaObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        if (entry.target.matches("img[data-src]")) loadDeferredImage(entry.target);
        else loadVideoPoster(entry.target);
        mediaObserver.unobserve(entry.target);
      });
    }, { rootMargin: mobileLite ? "160px 0px" : "600px 0px" });
    $$('img[data-src], video[data-poster]').forEach((media) => mediaObserver.observe(media));
  } else {
    $$('img[data-src]').forEach(loadDeferredImage);
    $$('video[data-poster]').forEach(loadVideoPoster);
  }

  // Bereid alleen de twee late, zelfstandige video's kort vóór het viewport
  // voor. Zo heeft Safari de eerste frames al klaar wanneer 50% zichtbaar is,
  // zonder dat deze videobestanden de eerste paginalaad vertragen.
  const prewarmVideo = (video) => {
    if (!video || video.dataset.mediaReady === "1") return;
    video.playsInline = true;
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute("muted", "");
    video.preload = "metadata";
    ensureVideo(video);
  };
  const prewarmVideos = allVideos.filter((video) => video.matches("[data-video-prewarm]"));
  if ("IntersectionObserver" in window) {
    const prewarmByTrigger = new Map();
    prewarmVideos.forEach((video) => {
      const trigger = video.dataset.prewarmTrigger ? $(video.dataset.prewarmTrigger) : video;
      const videos = prewarmByTrigger.get(trigger) || [];
      videos.push(video);
      prewarmByTrigger.set(trigger, videos);
    });
    const videoPrewarmObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        (prewarmByTrigger.get(entry.target) || []).forEach(prewarmVideo);
        videoPrewarmObserver.unobserve(entry.target);
      });
    }, { threshold: 0, rootMargin: useLiteVideo ? "0px" : "160px 0px" });
    prewarmByTrigger.forEach((videos, trigger) => videoPrewarmObserver.observe(trigger));
  } else {
    prewarmVideos.forEach(prewarmVideo);
  }

  // Continu bewegende elementen blijven zichtbaar animeren, maar kosten buiten
  // beeld geen compositing- of painttijd op oudere telefoons.
  if ("IntersectionObserver" in window) {
    const motionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        entry.target.classList.toggle("is-motion-paused", !entry.isIntersecting);
      });
    }, { threshold: 0, rootMargin: "0px" });
    const motionRegions = new Set($$("main > section, .logos, .logos__frame, .belt, [data-marquee-row], .wild, .wild__strip"));
    motionRegions.forEach((region) => motionObserver.observe(region));
  }

  /* ---------- scroll-voortgangsbalk ---------- */
  const scrollprog = $("[data-scrollprog]");
  if (scrollprog) {
    const footer = $(".footer");
    let progTick = false;
    let progMetricTick = false;
    let progMax = 1;
    let progSteps = 1;
    let lastProgPixel = -1;
    const refreshProgMetrics = () => {
      const footerTop = footer ? footer.offsetTop : document.documentElement.scrollHeight;
      progMax = Math.max(1, footerTop - window.innerHeight);
      const cssWidth = scrollprog.getBoundingClientRect().width || window.innerWidth || 1;
      progSteps = Math.max(1, Math.round(cssWidth * Math.min(2, window.devicePixelRatio || 1)));
      lastProgPixel = -1;
    };
    const updateProg = () => {
      progTick = false;
      const ratio = Math.min(1, Math.max(0, window.scrollY / progMax));
      const pixel = Math.round(ratio * progSteps);
      if (pixel === lastProgPixel) return;
      lastProgPixel = pixel;
      scrollprog.style.transform = `scaleX(${pixel / progSteps})`;
    };
    const queueProg = () => {
      if (!progTick) { progTick = true; requestAnimationFrame(updateProg); }
    };
    window.addEventListener("scroll", queueProg, { passive: true });
    const refreshProg = () => {
      if (progMetricTick) return;
      progMetricTick = true;
      window.requestAnimationFrame(() => {
        progMetricTick = false;
        refreshProgMetrics();
        queueProg();
      });
    };
    window.addEventListener("resize", refreshProg, { passive: true });
    window.addEventListener("load", refreshProg, { once: true });
    if ("ResizeObserver" in window) {
      const progressResizeObserver = new ResizeObserver(refreshProg);
      if (footer) progressResizeObserver.observe(footer);
      const main = $("main");
      if (main) progressResizeObserver.observe(main);
    }
    refreshProgMetrics();
    updateProg();
  }

  /* ---------- recente-activiteit ticker (social proof) ---------- */
  const activity = $("[data-activity]");
  const activityItems = Array.isArray(nbAssetConfig.activity) ? nbAssetConfig.activity : [];
  if (activity && !reduceMotion && activityItems.length) {
    const nameEl = $("[data-activity-name]", activity);
    const textEl = $("[data-activity-text]", activity);
    const items = activityItems;
    let ai = 0;
    let started = false;
    const showOne = () => {
      const [n, t] = items[ai % items.length];
      ai += 1;
      nameEl.textContent = n;
      textEl.textContent = t;
      activity.classList.add("is-in");
      window.setTimeout(() => activity.classList.remove("is-in"), 5000);
      window.setTimeout(showOne, 12000);
    };
    // pas starten zodra de bezoeker iets voorbij de hero is (koop-intentie)
    const startActivity = () => {
      if (started || window.scrollY < window.innerHeight * 0.7) return;
      started = true;
      window.removeEventListener("scroll", startActivity);
      window.setTimeout(showOne, 1200);
    };
    window.addEventListener("scroll", startActivity, { passive: true });
  }

  /* ---------- klik-ripple op knoppen ---------- */
  if (!reduceMotion) {
    $$(".btn").forEach((btn) => {
      btn.addEventListener("pointerdown", (e) => {
        const r = btn.getBoundingClientRect();
        const size = Math.max(r.width, r.height);
        const ripple = document.createElement("span");
        ripple.className = "ripple";
        ripple.style.width = ripple.style.height = size + "px";
        ripple.style.left = e.clientX - r.left - size / 2 + "px";
        ripple.style.top = e.clientY - r.top - size / 2 + "px";
        btn.appendChild(ripple);
        window.setTimeout(() => ripple.remove(), 620);
      });
    });
  }

  /* ---------- magnetische bestelknop (desktop, subtiel) ---------- */
  if (!reduceMotion && window.matchMedia("(pointer: fine)").matches) {
    const magnet = $(".hero__cta .btn--primary");
    if (magnet) {
      let magnetRect = null;
      let magnetFrame = 0;
      let magnetX = 0;
      let magnetY = 0;
      magnet.addEventListener("pointerenter", () => {
        magnetRect = magnet.getBoundingClientRect();
      });
      magnet.addEventListener("pointermove", (e) => {
        magnetX = e.clientX;
        magnetY = e.clientY;
        if (magnetFrame) return;
        magnetFrame = window.requestAnimationFrame(() => {
          magnetFrame = 0;
          if (!magnetRect) return;
          const mx = (magnetX - magnetRect.left - magnetRect.width / 2) / magnetRect.width;
          const my = (magnetY - magnetRect.top - magnetRect.height / 2) / magnetRect.height;
          magnet.style.transform = `translate(${(mx * 8).toFixed(1)}px, ${(my * 6).toFixed(1)}px)`;
        });
      });
      magnet.addEventListener("pointerleave", () => {
        if (magnetFrame) window.cancelAnimationFrame(magnetFrame);
        magnetFrame = 0;
        magnetRect = null;
        magnet.style.transform = "";
      });
    }
  }

  /* ---------- header ---------- */
  const header = $("[data-header]");
  let headerScrolled = null;
  const setHeaderScrolled = (next) => {
    if (next === headerScrolled) return;
    headerScrolled = next;
    header.classList.toggle("is-scrolled", next);
  };
  setHeaderScrolled(window.scrollY > 10);
  const topbar = $(".topbar");
  if (topbar && "IntersectionObserver" in window) {
    // De zichtbaarheid van de vaste topbar verandert maar twee keer per
    // scrolltraject. Dat vervangt een callback op ieder scrollframe, terwijl
    // de glasheader praktisch op dezelfde 10px-drempel inschakelt.
    new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (entry) setHeaderScrolled(entry.intersectionRatio < 0.74);
    }, { threshold: [0, 0.74, 1] }).observe(topbar);
  } else {
    let headerTick = false;
    const queueHeader = () => {
      if (headerTick) return;
      headerTick = true;
      window.requestAnimationFrame(() => {
        headerTick = false;
        setHeaderScrolled(window.scrollY > 10);
      });
    };
    window.addEventListener("scroll", queueHeader, { passive: true });
  }

  /* ---------- functionele header + mobiel menu ---------- */
  const menu = $("[data-menu]");
  const menuBtn = $("[data-menu-btn]");
  const pagesMenu = $("[data-pages-menu]");
  const pagesBtn = $("[data-pages-btn]");
  const pagesPanel = $("[data-pages-panel]");
  let menuFocusReturn = null;

  const syncPageScrollLock = () => {
    const cartDialog = $("[data-nb-cart-dialog]");
    const locked = (menu && !menu.hidden) || Boolean(cartDialog && cartDialog.open);
    document.body.style.overflow = locked ? "hidden" : "";
  };

  const setPagesMenu = (open, { restoreFocus = false } = {}) => {
    if (!pagesBtn || !pagesPanel) return;
    pagesPanel.hidden = !open;
    pagesBtn.setAttribute("aria-expanded", String(open));
    if (!open && restoreFocus) pagesBtn.focus({ preventScroll: true });
  };

  const setMenu = (open, { restoreFocus = true } = {}) => {
    if (!menu || !menuBtn) return;
    if (open) {
      menuFocusReturn = document.activeElement;
      setPagesMenu(false);
    }
    menu.hidden = !open;
    menu.toggleAttribute("inert", !open);
    menu.setAttribute("aria-hidden", String(!open));
    menuBtn.setAttribute("aria-expanded", String(open));
    header.classList.toggle("is-menu-open", open);
    if (open) {
      // Meet ná de open-state: WebKit gebruikt daar position:fixed, zodat de
      // menuruimte exact onder de zichtbare header start, ook na lang scrollen.
      menu.style.setProperty("--menu-top", `${Math.max(0, header.getBoundingClientRect().bottom).toFixed(0)}px`);
    }
    const menuLabel = $(".sr-only", menuBtn);
    if (menuLabel) menuLabel.textContent = open ? menuBtn.dataset.menuCloseLabel : menuBtn.dataset.menuOpenLabel;
    syncPageScrollLock();
    if (open) {
      window.requestAnimationFrame(() => {
        const firstLink = $("a", menu);
        if (firstLink) firstLink.focus({ preventScroll: true });
      });
    } else if (restoreFocus && menuFocusReturn && typeof menuFocusReturn.focus === "function") {
      menuFocusReturn.focus({ preventScroll: true });
    }
  };

  if (pagesBtn && pagesPanel) {
    pagesBtn.addEventListener("click", () => setPagesMenu(pagesPanel.hidden));
    $$("a", pagesPanel).forEach((link) => link.addEventListener("click", () => setPagesMenu(false)));
  }
  if (menuBtn && menu) {
    menuBtn.addEventListener("click", () => setMenu(menu.hidden));
    menu.addEventListener("click", (event) => {
      if (event.target === menu) setMenu(false);
    });
    $$("[data-menu-link]", menu).forEach((link) =>
      link.addEventListener("click", () => setMenu(false, { restoreFocus: false }))
    );
    $$("[data-buy]", menu).forEach((button) =>
      button.addEventListener("click", () => setMenu(false, { restoreFocus: false }))
    );
  }

  document.addEventListener("click", (event) => {
    if (pagesMenu && pagesPanel && !pagesPanel.hidden && !pagesMenu.contains(event.target)) {
      setPagesMenu(false);
    }
  });
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    if (menu && !menu.hidden) {
      setMenu(false);
    } else if (pagesPanel && !pagesPanel.hidden) {
      setPagesMenu(false, { restoreFocus: true });
    }
  });

  const wideHeader = window.matchMedia("(min-width: 1041px)");
  const syncHeaderMode = () => {
    if (wideHeader.matches) setMenu(false, { restoreFocus: false });
    else setPagesMenu(false);
  };
  if (typeof wideHeader.addEventListener === "function") wideHeader.addEventListener("change", syncHeaderMode);
  else if (typeof wideHeader.addListener === "function") wideHeader.addListener(syncHeaderMode);

  // Eén IntersectionObserver houdt de compacte sectienavigatie actueel. Dit
  // voegt geen extra scrollhandler of layout-loop toe op oudere apparaten.
  const sectionLinks = $$("[data-section-link]");
  const sectionTargets = new Map();
  sectionLinks.forEach((link) => {
    const id = link.getAttribute("href");
    if (!id || id.charAt(0) !== "#" || sectionTargets.has(id)) return;
    const target = $(id);
    if (target) sectionTargets.set(id, target);
  });
  const setActiveSection = (id) => {
    sectionLinks.forEach((link) => {
      const active = link.getAttribute("href") === id;
      link.classList.toggle("is-active", active);
      if (active) link.setAttribute("aria-current", "location");
      else link.removeAttribute("aria-current");
    });
  };
  if ("IntersectionObserver" in window && sectionTargets.size) {
    const sectionObserver = new IntersectionObserver((entries) => {
      const current = entries.find((entry) => entry.isIntersecting);
      if (current) setActiveSection(`#${current.target.id}`);
    }, { threshold: 0, rootMargin: "-24% 0px -66% 0px" });
    sectionTargets.forEach((target) => sectionObserver.observe(target));
  }
  sectionLinks.forEach((link) =>
    link.addEventListener("click", () => setActiveSection(link.getAttribute("href")))
  );

  /* ---------- reveal on scroll ---------- */
  const revealEls = $$(".reveal");
  if (reduceMotion) {
    revealEls.forEach((el) => el.classList.add("is-in"));
  } else {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -6% 0px" }
    );
    revealEls.forEach((el) => io.observe(el));
  }

  /* ---------- tellers ---------- */
  const counters = $$("[data-count]");
  const runCounter = (el) => {
    const target = Number(el.dataset.count);
    if (!Number.isFinite(target)) return;
    const prefix = el.dataset.prefix || "";
    const suffix = el.dataset.suffix || "";
    const decimals = clamp(Math.floor(Number(el.dataset.decimals) || 0), 0, 3);
    const decimalSeparator = el.dataset.decimalSeparator || ".";
    const formatCounter = (value) => {
      const output = decimals ? value.toFixed(decimals) : String(Math.round(value));
      return prefix + output.replace(".", decimalSeparator) + suffix;
    };
    if (reduceMotion) {
      el.textContent = formatCounter(target);
      return;
    }
    const t0 = performance.now();
    const dur = 1100;
    const tick = (t) => {
      const p = clamp((t - t0) / dur, 0, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = formatCounter(target * eased);
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };
  if (counters.length) {
    const cio = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            runCounter(e.target);
            cio.unobserve(e.target);
          }
        });
      },
      { threshold: 0.6 }
    );
    counters.forEach((el) => cio.observe(el));
  }

  /* ---------- de Nordic curl ----------
     Renderer 1-op-1 geport van de live nordbelt.nl-homepage (canvas-animatie);
     kleuren omgezet naar het poolnacht-palet van deze sectie.
     Loopt autonoom (6,4s-cyclus, zoals op de site) zodra hij in beeld is. */
  const move = $("[data-move]");
  if (move) {
    const canvas = $("[data-nhc-canvas]", move);
    const ctx = canvas ? canvas.getContext("2d", { alpha: true, desynchronized: true }) : null;
    const loadEl = $("[data-curl-load]", move);
    const barEl = $("[data-curl-bar]", move);
    const phases = $$(".move__phases li", move);

    if (canvas && ctx) {
      // — geometrie & timing: overgenomen van nordbelt.nl —
      const DESIGN_W = 800;
      const DESIGN_H = 486;
      const TORSO_WIDTH = 30, THIGH_WIDTH = 25, SHIN_WIDTH = 21;
      const UPPER_ARM_WIDTH = 16, FOREARM_WIDTH = 13;
      const SHIN_LEN = 158, FOOT_LEN = 34, THIGH_LEN = 152, TORSO_LEN = 175;
      const UPPER_ARM_LEN = 92, FOREARM_LEN = 63;
      const KNEE_X = 270, GROUND_Y = 448, KNEE_Y = GROUND_Y;
      const TOP_ANGLE = (115 * Math.PI) / 180;
      const BOTTOM_ANGLE = (18 * Math.PI) / 180;
      const RANGE = TOP_ANGLE - BOTTOM_ANGLE;
      const ECCENTRIC_FRAC = 0.75;
      const REST_FRAC = 0.3;
      const REST_TORSO_WORLD = (87 * Math.PI) / 180;
      const BOTTOM_TORSO_WORLD = (-1 * Math.PI) / 180;

      // — palet: zacht ijs + kalm cobalt; canvas zelf is transparant —
      const BODY_COLOR = "#d9e4f2";
      const SHIRT_COLOR = "#4a63d8";
      const SHORTS_COLOR = "#31418f";
      const SHOE_COLOR = "#b6c7dc";
      const GLOW_RGB = "74, 99, 216";
      const GLOW_SOFT_RGB = "150, 200, 235";
      const CYCLE_MS = 6400;

      const noiseConfig = {
        thigh: { freq: 1.7, phase: 0.0, amp: 0.01 },
        torso: { freq: 2.3, phase: 1.1, amp: 0.014 },
        head: { freq: 3.1, phase: 2.5, amp: 0.02 },
        arm: { freq: 2.0, phase: 0.7, amp: 0.012 },
      };

      let dpr = 1;
      let scale = 1;
      let groundFill = null;
      const hudCache = document.createElement("canvas");
      const hudCacheCtx = hudCache.getContext("2d", { alpha: true });
      let hudCacheRep = -1;
      let hudCacheScale = -1;

      const smootherStep = (t) => {
        t = clamp(t, 0, 1);
        return t * t * t * (t * (t * 6 - 15) + 10);
      };
      const eccentricEase = (t) => 1 - Math.pow(1 - t, 3.5);
      const getNoise = (name, timeSec) => {
        const cfg = noiseConfig[name];
        return Math.sin(timeSec * cfg.freq * Math.PI * 2 + cfg.phase) * cfg.amp;
      };

      const getThighAngle = (cycleT) => {
        if (cycleT < REST_FRAC) return TOP_ANGLE;
        const activeT = (cycleT - REST_FRAC) / (1 - REST_FRAC);
        if (activeT < ECCENTRIC_FRAC) {
          return TOP_ANGLE - RANGE * eccentricEase(activeT / ECCENTRIC_FRAC);
        }
        const concentric = (activeT - ECCENTRIC_FRAC) / (1 - ECCENTRIC_FRAC);
        return BOTTOM_ANGLE + RANGE * smootherStep(concentric);
      };

      const getTorsoRel = (thighAngle) => {
        const progress = 1 - (thighAngle - BOTTOM_ANGLE) / RANGE;
        const restRel = TOP_ANGLE - REST_TORSO_WORLD;
        const bottomRel = BOTTOM_ANGLE - BOTTOM_TORSO_WORLD;
        return restRel + progress * progress * (bottomRel - restRel);
      };

      const drawLine = (x1, y1, x2, y2) => {
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      };
      const drawLimb = (x1, y1, x2, y2, width) => {
        ctx.save();
        ctx.lineWidth = width * scale;
        ctx.lineCap = "round";
        drawLine(x1, y1, x2, y2);
        ctx.restore();
      };
      const drawJoint = (x, y, radius) => {
        ctx.beginPath();
        ctx.arc(x, y, radius * scale, 0, Math.PI * 2);
        ctx.fill();
      };
      const drawLimbHighlight = (x1, y1, x2, y2, width) => {
        const limbWidth = width * scale;
        const dx = x2 - x1;
        const dy = y2 - y1;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len < 1) return;
        const ox = (-dy / len) * limbWidth * 0.22;
        const oy = (dx / len) * limbWidth * 0.22;
        ctx.save();
        ctx.strokeStyle = "rgba(255, 255, 255, 0.24)";
        ctx.lineWidth = limbWidth * 0.3;
        ctx.lineCap = "round";
        drawLine(x1 + ox, y1 + oy, x2 + ox, y2 + oy);
        ctx.restore();
      };

      const drawHead = (shoulderX, shoulderY, angle) => {
        const radius = 31 * scale;
        const cx = shoulderX + Math.cos(angle) * radius;
        const cy = shoulderY - Math.sin(angle) * radius;
        ctx.save();
        ctx.fillStyle = BODY_COLOR;
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#93aecb";
        ctx.beginPath();
        ctx.arc(cx, cy, radius * 0.66, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = SHIRT_COLOR;
        ctx.beginPath();
        ctx.arc(cx, cy, radius * 1.06, -angle + Math.PI * 0.5, -angle - Math.PI * 0.5, false);
        ctx.arc(cx, cy, radius * 0.7, -angle - Math.PI * 0.5, -angle + Math.PI * 0.5, true);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      };

      const drawShoe = (ankleX, ankleY, groundY) => {
        ctx.save();
        ctx.strokeStyle = SHOE_COLOR;
        ctx.lineWidth = 24 * scale;
        ctx.lineCap = "round";
        drawLine(ankleX, ankleY, ankleX, groundY - 1.5 * scale);
        ctx.restore();
      };

      const drawBackground = (timeSec) => {
        const gradient = ctx.createLinearGradient(0, 0, canvas.width * 0.2, canvas.height);
        gradient.addColorStop(0, "#101d33");
        gradient.addColorStop(0.55, "#0c1626");
        gradient.addColorStop(1, "#0a1322");
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const glowA = ctx.createRadialGradient(canvas.width * 0.12, canvas.height * 0.11, 0, canvas.width * 0.12, canvas.height * 0.11, canvas.width * 0.62);
        glowA.addColorStop(0, "rgba(165, 233, 255, 0.10)");
        glowA.addColorStop(0.55, "rgba(165, 233, 255, 0.03)");
        glowA.addColorStop(1, "rgba(165, 233, 255, 0)");
        ctx.fillStyle = glowA;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const pulse = 0.5 + Math.sin(timeSec * 0.65) * 0.5;
        const glowB = ctx.createRadialGradient(canvas.width * 0.83, canvas.height * 0.18, 0, canvas.width * 0.83, canvas.height * 0.18, canvas.width * 0.48);
        glowB.addColorStop(0, "rgba(" + GLOW_RGB + ", " + (0.20 + pulse * 0.07) + ")");
        glowB.addColorStop(1, "rgba(" + GLOW_RGB + ", 0)");
        ctx.fillStyle = glowB;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      };

      const drawGround = (groundY) => {
        const s = scale;
        ctx.fillStyle = groundFill || "rgba(165, 233, 255, 0.045)";
        ctx.fillRect(0, groundY, canvas.width, canvas.height - groundY);
        ctx.strokeStyle = "rgba(165, 233, 255, 0.4)";
        ctx.lineWidth = 2 * s;
        drawLine(18 * s, groundY, canvas.width - 18 * s, groundY);
        ctx.strokeStyle = "rgba(255, 255, 255, 0.08)";
        ctx.lineWidth = 1 * s;
        drawLine(18 * s, groundY - 1.5 * s, canvas.width - 18 * s, groundY - 1.5 * s);
      };

      const computePose = (thighBase, timeSec) => {
        const s = scale;
        const kneeX = KNEE_X * s;
        const kneeY = KNEE_Y * s;
        const groundY = GROUND_Y * s;

        const thighA = thighBase + getNoise("thigh", timeSec);
        const torsoRel = getTorsoRel(thighA) + getNoise("torso", timeSec);
        const torsoWorld = thighA - torsoRel;

        const shinAngle = Math.PI - Math.asin(FOOT_LEN / SHIN_LEN);
        const ankleX = kneeX + Math.cos(shinAngle) * SHIN_LEN * s;
        const ankleY = kneeY - Math.sin(shinAngle) * SHIN_LEN * s;

        const hipX = kneeX + Math.cos(thighA) * THIGH_LEN * s;
        const hipY = kneeY - Math.sin(thighA) * THIGH_LEN * s;

        const shoulderX = hipX + Math.cos(torsoWorld) * TORSO_LEN * s;
        const shoulderY = hipY - Math.sin(torsoWorld) * TORSO_LEN * s;

        const armAttachX = shoulderX + (hipX - shoulderX) * 0.2;
        const armAttachY = shoulderY + (hipY - shoulderY) * 0.2;
        const armAngle = torsoWorld + Math.PI + (25 * Math.PI) / 180 + getNoise("arm", timeSec);
        let elbowX = armAttachX + Math.cos(armAngle) * UPPER_ARM_LEN * s;
        let elbowY = armAttachY - Math.sin(armAngle) * UPPER_ARM_LEN * s;
        const tilt = clamp(torsoWorld / (Math.PI / 2), 0, 1);
        const elbowBend = (170 * Math.PI) / 180 + tilt * (Math.PI / 2 - (170 * Math.PI) / 180);
        const forearmAngle = armAngle + elbowBend;
        let wristX = elbowX + Math.cos(forearmAngle) * FOREARM_LEN * s;
        let wristY = elbowY - Math.sin(forearmAngle) * FOREARM_LEN * s;
        if (elbowY > groundY) elbowY = groundY;
        if (wristY > groundY) wristY = groundY;

        return {
          kneeX, kneeY, ankleX, ankleY, hipX, hipY,
          shoulderX, shoulderY, armAttachX, armAttachY,
          elbowX, elbowY, wristX, wristY, torsoWorld,
          headNoise: getNoise("head", timeSec), groundY,
        };
      };

      const drawFigureShadow = (pose) => {
        const spread = Math.abs(pose.shoulderX - pose.ankleX) + 42 * scale;
        const centerX = (pose.kneeX + pose.shoulderX) / 2;
        if (lowPowerDevice) {
          ctx.fillStyle = "rgba(74, 99, 216, 0.13)";
        } else {
          const gradient = ctx.createRadialGradient(centerX, pose.groundY + 1.5 * scale, 0, centerX, pose.groundY + 1.5 * scale, spread / 2);
          gradient.addColorStop(0, "rgba(74, 99, 216, 0.22)");
          gradient.addColorStop(0.6, "rgba(74, 99, 216, 0.09)");
          gradient.addColorStop(1, "rgba(74, 99, 216, 0)");
          ctx.fillStyle = gradient;
        }
        ctx.beginPath();
        ctx.ellipse(centerX, pose.groundY + 1.5 * scale, spread / 2, 8 * scale, 0, 0, Math.PI * 2);
        ctx.fill();
      };

      const drawFigure = (pose, timeSec, eccProgress) => {
        drawFigureShadow(pose);

        if (eccProgress > 0.01 && eccProgress < 1) {
          const pulse = 0.5 + 0.5 * Math.sin(timeSec * 6.2);
          const ramp = Math.min(1, eccProgress * 3.9);
          const fade = 1 - Math.pow(Math.max(0, eccProgress - 0.9) / 0.1, 2);
          const intensity = ramp * fade * (0.84 + 0.34 * pulse);
          ctx.save();
          ctx.strokeStyle = "rgba(" + GLOW_RGB + ", " + intensity * 0.7 + ")";
          if (!lowPowerDevice) {
            ctx.shadowColor = "rgba(" + GLOW_RGB + ", " + intensity * 0.42 + ")";
            ctx.shadowBlur = (24 + 22 * intensity) * scale;
          }
          ctx.lineWidth = (THIGH_WIDTH + 44) * scale;
          ctx.lineCap = "round";
          drawLine(pose.kneeX, pose.kneeY, pose.hipX, pose.hipY);
          ctx.restore();

          ctx.save();
          ctx.strokeStyle = "rgba(" + GLOW_SOFT_RGB + ", " + intensity * 0.4 + ")";
          ctx.lineWidth = (THIGH_WIDTH + 27) * scale;
          ctx.lineCap = "round";
          drawLine(pose.kneeX, pose.kneeY, pose.hipX, pose.hipY);
          ctx.restore();

          ctx.save();
          ctx.strokeStyle = "rgba(255, 122, 100, " + intensity * 0.24 + ")";
          ctx.lineWidth = (THIGH_WIDTH + 10) * scale;
          ctx.lineCap = "round";
          drawLine(pose.kneeX, pose.kneeY, pose.hipX, pose.hipY);
          ctx.restore();
        }

        ctx.strokeStyle = BODY_COLOR;
        ctx.fillStyle = BODY_COLOR;
        drawLimb(pose.kneeX, pose.kneeY, pose.ankleX, pose.ankleY, SHIN_WIDTH);
        drawJoint(pose.ankleX, pose.ankleY, SHIN_WIDTH / 2);
        drawShoe(pose.ankleX, pose.ankleY, pose.groundY);

        ctx.strokeStyle = SHORTS_COLOR;
        ctx.fillStyle = SHORTS_COLOR;
        drawLimb(pose.kneeX, pose.kneeY, pose.hipX, pose.hipY, THIGH_WIDTH);
        drawJoint(pose.kneeX, pose.kneeY, THIGH_WIDTH / 2);
        drawJoint(pose.hipX, pose.hipY, THIGH_WIDTH / 2);

        ctx.strokeStyle = SHIRT_COLOR;
        ctx.fillStyle = SHIRT_COLOR;
        drawLimb(pose.hipX, pose.hipY, pose.shoulderX, pose.shoulderY, TORSO_WIDTH);

        ctx.strokeStyle = BODY_COLOR;
        ctx.fillStyle = BODY_COLOR;
        drawLimb(pose.armAttachX, pose.armAttachY, pose.elbowX, pose.elbowY, UPPER_ARM_WIDTH);
        drawJoint(pose.elbowX, pose.elbowY, UPPER_ARM_WIDTH / 2);
        drawLimb(pose.elbowX, pose.elbowY, pose.wristX, pose.wristY, FOREARM_WIDTH);

        ctx.save();
        ctx.strokeStyle = "#d6e7f8";
        ctx.lineWidth = 20 * scale;
        ctx.lineCap = "round";
        drawLine(pose.wristX, pose.wristY, pose.wristX, pose.wristY + 1 * scale);
        ctx.restore();

        drawLimbHighlight(pose.kneeX, pose.kneeY, pose.ankleX, pose.ankleY, SHIN_WIDTH);
        drawLimbHighlight(pose.kneeX, pose.kneeY, pose.hipX, pose.hipY, THIGH_WIDTH);
        drawLimbHighlight(pose.hipX, pose.hipY, pose.shoulderX, pose.shoulderY, TORSO_WIDTH);
        drawLimbHighlight(pose.armAttachX, pose.armAttachY, pose.elbowX, pose.elbowY, UPPER_ARM_WIDTH);

        drawHead(pose.shoulderX, pose.shoulderY, pose.torsoWorld + pose.headNoise);
      };

      const refreshHudCache = (repCount) => {
        if (!hudCacheCtx) return;
        const s = scale;
        const nextWidth = Math.max(1, Math.ceil(184 * s));
        const nextHeight = Math.max(1, Math.ceil(176 * s));
        if (hudCache.width !== nextWidth || hudCache.height !== nextHeight) {
          hudCache.width = nextWidth;
          hudCache.height = nextHeight;
        } else {
          hudCacheCtx.clearRect(0, 0, nextWidth, nextHeight);
        }
        const ringRadius = 62 * s;
        const ringWidth = 11 * s;
        const centerX = hudCache.width - 86 * s;
        const centerY = 78 * s;
        const outerRadius = ringRadius + 10 * s;
        const glow = hudCacheCtx.createRadialGradient(
          centerX, centerY, ringRadius * 0.15,
          centerX, centerY, ringRadius * 1.5
        );
        glow.addColorStop(0, "rgba(165, 233, 255, 0.14)");
        glow.addColorStop(1, "rgba(165, 233, 255, 0)");

        hudCacheCtx.save();
        hudCacheCtx.fillStyle = glow;
        hudCacheCtx.beginPath();
        hudCacheCtx.arc(centerX, centerY, ringRadius * 1.52, 0, Math.PI * 2);
        hudCacheCtx.fill();

        hudCacheCtx.fillStyle = "rgba(10, 19, 34, 0.85)";
        hudCacheCtx.strokeStyle = "rgba(165, 233, 255, 0.28)";
        hudCacheCtx.lineWidth = 1.1 * s;
        hudCacheCtx.beginPath();
        hudCacheCtx.arc(centerX, centerY, outerRadius, 0, Math.PI * 2);
        hudCacheCtx.fill();
        hudCacheCtx.stroke();

        hudCacheCtx.strokeStyle = "rgba(165, 233, 255, 0.16)";
        hudCacheCtx.lineWidth = ringWidth;
        hudCacheCtx.lineCap = "round";
        hudCacheCtx.beginPath();
        hudCacheCtx.arc(centerX, centerY, ringRadius, -Math.PI / 2, Math.PI * 1.5);
        hudCacheCtx.stroke();

        const centerFill = hudCacheCtx.createLinearGradient(
          centerX, centerY - ringRadius,
          centerX, centerY + ringRadius
        );
        centerFill.addColorStop(0, "#101d33");
        centerFill.addColorStop(1, "#0a1322");
        hudCacheCtx.fillStyle = centerFill;
        hudCacheCtx.beginPath();
        hudCacheCtx.arc(centerX, centerY, ringRadius - ringWidth * 0.92, 0, Math.PI * 2);
        hudCacheCtx.fill();

        const digits = String(repCount);
        const numberSize = digits.length > 2 ? 26 * s : 34 * s;
        hudCacheCtx.fillStyle = "#dce7f5";
        hudCacheCtx.font = "800 " + numberSize + 'px "Bricolage Grotesque", "Avenir Next", sans-serif';
        hudCacheCtx.textAlign = "center";
        hudCacheCtx.textBaseline = "middle";
        hudCacheCtx.fillText(digits, centerX, centerY - 3 * s);

        hudCacheCtx.font = "600 " + 14 * s + 'px "JetBrains Mono", monospace';
        hudCacheCtx.fillStyle = "rgba(202, 222, 244, 0.65)";
        hudCacheCtx.fillText("reps", centerX, centerY + 21 * s);
        hudCacheCtx.restore();
        hudCacheRep = repCount;
        hudCacheScale = scale;
      };

      const drawHud = (progress, repCount) => {
        const s = scale;
        if (hudCacheRep !== repCount || hudCacheScale !== scale) refreshHudCache(repCount);
        if (hudCacheCtx) ctx.drawImage(hudCache, canvas.width - hudCache.width, 0);

        const ringRadius = 62 * s;
        const centerX = canvas.width - 86 * s;
        const centerY = 78 * s;
        const startAngle = -Math.PI / 2;
        ctx.save();
        ctx.strokeStyle = progress > 0.985 ? "#ff9a82" : "#96c8eb";
        ctx.lineWidth = 11 * s;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.arc(centerX, centerY, ringRadius, startAngle, startAngle + Math.PI * 2 * progress);
        ctx.stroke();
        ctx.restore();
      };

      const renderFrame = (cycleT, timeSec, repCount) => {
        if (!canvas.width || !canvas.height) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const pose = computePose(getThighAngle(cycleT), timeSec);
        drawGround(pose.groundY);
        const activeT = cycleT < REST_FRAC ? 0 : (cycleT - REST_FRAC) / (1 - REST_FRAC);
        const eccProgress = activeT > 0 && activeT < ECCENTRIC_FRAC ? activeT / ECCENTRIC_FRAC : 0;
        drawFigure(pose, timeSec, eccProgress);
        drawHud(clamp(activeT, 0, 1), repCount);
      };

      const refreshPaintCache = () => {
        const groundY = GROUND_Y * scale;
        groundFill = ctx.createLinearGradient(0, groundY, 0, canvas.height);
        groundFill.addColorStop(0, "rgba(165, 233, 255, 0.07)");
        groundFill.addColorStop(1, "rgba(165, 233, 255, 0.02)");

        hudCacheRep = -1;
        hudCacheScale = -1;
      };

      const resizeCanvas = (measuredWidth = 0) => {
        // CSS (width:100% + aspect-ratio) bepaalt de layout; hier alleen de bitmapresolutie
        const cssWidth = measuredWidth || canvas.getBoundingClientRect().width || DESIGN_W;
        if (cssWidth <= 0) return false;
        // Een DPR van 3 rendert op een telefoon ruim negen keer zoveel pixels
        // als DPR 1. De curl blijft scherp met deze begrenzing, terwijl oudere
        // GPU's veel minder canvaswerk hoeven te doen.
        const dprCap = lowPowerDevice ? (mobileViewport ? 1.25 : 1.4) : (mobileViewport ? 1.8 : 2);
        const nextDpr = Math.max(1, Math.min(dprCap, window.devicePixelRatio || 1));
        const nextWidth = Math.round(cssWidth * nextDpr);
        const nextHeight = Math.round(((cssWidth * DESIGN_H) / DESIGN_W) * nextDpr);
        // Het herschrijven van canvas.width/height wist de context en laat Edge
        // een nieuwe GPU-bitmap alloceren. ResizeObserver kan ook zonder echte
        // maatwijziging vuren, dus sla zulke reallocaties volledig over.
        if (canvas.width === nextWidth && canvas.height === nextHeight) return false;
        dpr = nextDpr;
        canvas.width = nextWidth;
        canvas.height = nextHeight;
        scale = Math.min(canvas.width / DESIGN_W, canvas.height / DESIGN_H);
        refreshPaintCache();
        return true;
      };

      // — autonome tijdlijn (zoals op de site) + live telemetrie —
      let elapsed = 0;
      let lastFrameTime = 0;
      let lastCycleT = REST_FRAC;
      let active = false;
      let rafId = null;
      let reps = 0;
      let lastPaintTime = 0;
      let lastTelemetryTime = 0;
      let lastLoadValue = -1;
      let lastBarScale = "";
      let lastPhase = -1;

      const countRep = () => { reps += 1; };

      const telemetry = (cycleT) => {
        const thighA = getThighAngle(cycleT);
        const lean = ((TOP_ANGLE - thighA) / RANGE) * 78;
        const load = 20 + (lean / 78) * 80;
        const loadValue = Math.round(load);
        if (loadValue !== lastLoadValue) {
          loadEl.textContent = `${loadValue}%`;
          lastLoadValue = loadValue;
        }
        const barScale = (load / 100).toFixed(3);
        if (barScale !== lastBarScale) {
          barEl.style.transform = `scaleX(${barScale})`;
          lastBarScale = barScale;
        }
        const activeT = cycleT < REST_FRAC ? 0 : (cycleT - REST_FRAC) / (1 - REST_FRAC);
        const phase = cycleT < REST_FRAC + 0.06 ? 0 : activeT < ECCENTRIC_FRAC ? 1 : 2;
        if (phase !== lastPhase) {
          phases.forEach((li, i) => li.classList.toggle("is-active", i === phase));
          lastPhase = phase;
        }
      };

      const frame = (now) => {
        if (!lastFrameTime) lastFrameTime = now;
        let delta = now - lastFrameTime;
        if (!isFinite(delta) || delta < 0) delta = 0;
        if (delta > 120) delta = 120;
        elapsed += delta;
        lastFrameTime = now;
        const completed = Math.floor(elapsed / CYCLE_MS);
        if (completed > reps) countRep();
        lastCycleT = (elapsed % CYCLE_MS) / CYCLE_MS;
        // De beweging is bewust langzaam. Op beperkte hardware is 30 fps
        // visueel vloeiend, maar halveert dit de canvasbelasting. DOM-telemetrie
        // draait maximaal 20 fps en gebruikt een compositor-transform.
        const paintDue = !lowPowerDevice || now - lastPaintTime >= 32;
        if (paintDue) {
          renderFrame(lastCycleT, elapsed / 1000, reps);
          lastPaintTime = now;
        }
        if (now - lastTelemetryTime >= (lowPowerDevice ? 80 : 50)) {
          telemetry(lastCycleT);
          lastTelemetryTime = now;
        }
        rafId = active ? requestAnimationFrame(frame) : null;
      };

      resizeCanvas();
      if ("ResizeObserver" in window) {
        let resizeFrame = 0;
        let pendingCanvasWidth = 0;
        new ResizeObserver((entries) => {
          const latest = entries[entries.length - 1];
          pendingCanvasWidth = latest && latest.contentRect ? latest.contentRect.width : 0;
          if (resizeFrame) return;
          resizeFrame = window.requestAnimationFrame(() => {
            resizeFrame = 0;
            const changed = resizeCanvas(pendingCanvasWidth);
            if (changed && !active) {
              renderFrame(lastCycleT, elapsed / 1000, reps);
              telemetry(lastCycleT);
            }
          });
        }).observe(canvas.parentElement);
      }

      // De NHC-demonstratie is productuitleg, geen decoratief effect. Laat hem
      // daarom ook bij een door Windows/Edge afgedwongen reduced-motion-profiel
      // lopen; buiten beeld blijft requestAnimationFrame volledig gepauzeerd.
      const mio = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            active = e.intersectionRatio >= 0.12;
            if (active && rafId === null) {
              lastFrameTime = 0; // geen tijdsprong na uit beeld
              lastPaintTime = 0;
              lastTelemetryTime = 0;
              rafId = requestAnimationFrame(frame);
            }
          });
        },
        { threshold: 0.12 }
      );
      mio.observe(move);
      // Teken de zware canvas-scène pas wanneer de uitleg werkelijk in beeld
      // komt. De observer start dan direct dezelfde animatie; boven de vouw
      // besparen we op trage telefoons een complete offscreen render.
    }
  }

  /* ---------- video's: toggle + autoplay in beeld ---------- */
  $$("[data-video-card]").forEach((card) => {
    const video = $("video", card);
    const btn = $("[data-video-toggle]", card);
    if (!video || !btn) return;
    const toggle = () => {
      const sequence = card.closest("[data-sequence]");
      if (sequence && video.paused) {
        video.dataset.userPaused = "";
        const index = $$('[data-video-card]', sequence).indexOf(card);
        sequence.dispatchEvent(new CustomEvent("nb:sequence-select", { detail: { index } }));
        return;
      }
      const justPrimed = Date.now() - Number(video.dataset.gesturePrimedAt || 0) < 1000;
      if (justPrimed && !video.paused) return;
      if (video.paused) {
        video.dataset.userPaused = "";
        safePlay(video);
      } else {
        video.dataset.userPaused = "1";
        stopVideo(video, "paused-by-user");
        if (sequence) {
          const index = $$('[data-video-card]', sequence).indexOf(card);
          sequence.dispatchEvent(new CustomEvent("nb:sequence-poster", { detail: { index } }));
        }
      }
    };
    btn.addEventListener("click", toggle);
    video.addEventListener("click", toggle);
    video.addEventListener("play", () => {
      if (video.dataset.gesturePriming === "1") {
        if (videoMayPlay(video)) card.classList.add("is-playing");
        return;
      }
      // Een vertraagde Safari-autoplaybelofte mag een video die inmiddels uit
      // beeld is niet opnieuw laten starten.
      if (video.dataset.playWanted === "0" || !videoMayPlay(video)) {
        stopVideo(video);
        return;
      }
      card.classList.add("is-playing");
    });
    video.addEventListener("pause", () => {
      card.classList.remove("is-playing");
      if (card.closest("[data-sequence]")) card.classList.remove("is-video-ready");
    });
    video.addEventListener("playing", () => {
      mediaSessionUnlocked = true;
      detachMediaPrime();
      video.dataset.autoplayState = "playing";
      if (card.closest("[data-sequence]")) card.classList.add("is-video-ready");
    });
  });

  // Laat de eerste contentpaint vóór de hoogwaardige hero-video gaan. De
  // poster staat al parser-vroeg klaar; autoplay hoeft niet te wachten tot
  // ieder laat beeld op de pagina het window-load-event heeft bereikt.
  let initialAutoplayReady = false;

  const autoplayVisibleVideos = () => {
    if (document.hidden || !initialAutoplayReady) return;
    nonSequenceAutoplayVideos.forEach((video) => {
      if (videoMayPlay(video) && video.paused && video.dataset.userPaused !== "1") {
        safePlay(video, { forceMuted: true });
      }
    });
  };

  const syncNonSequenceVideos = (fresh = false) => {
    nonSequenceAutoplayVideos.forEach((video) => {
      if (videoMayPlay(video, fresh)) {
        if (initialAutoplayReady && video.paused && video.dataset.userPaused !== "1") {
          safePlay(video, { forceMuted: true });
        }
      } else {
        stopVideo(video);
      }
    });
  };

  // Start alleen wanneer minstens 50% zichtbaar is en pauzeer weer zodra meer
  // dan de helft buiten beeld valt.
  if ("IntersectionObserver" in window) {
    const vio = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const video = e.target;
          visibilityRatios.set(video, e.intersectionRatio);
          const auto = video.closest("[data-autoplay]");
          const inSequence = video.closest("[data-sequence]");
          if (e.intersectionRatio >= 0.5) {
            if (initialAutoplayReady && auto && !inSequence && video.paused && video.dataset.userPaused !== "1") {
              safePlay(video, { forceMuted: true });
            }
          } else if (!inSequence) {
            stopVideo(video);
          }
        });
      },
      { threshold: [0, 0.5], rootMargin: "0px" }
    );
    allVideos.forEach((video) => vio.observe(video));
  } else {
    let fallbackFrame = 0;
    const scheduleFallbackSync = () => {
      if (fallbackFrame) return;
      fallbackFrame = window.requestAnimationFrame(() => {
        fallbackFrame = 0;
        syncNonSequenceVideos();
      });
    };
    window.addEventListener("scroll", scheduleFallbackSync, { passive: true });
    window.addEventListener("resize", scheduleFallbackSync);
    window.setTimeout(scheduleFallbackSync, 0);
  }

  const releaseInitialAutoplay = () => {
    if (initialAutoplayReady) return;
    initialAutoplayReady = true;
    autoplayVisibleVideos();
  };
  const scheduleInitialAutoplay = () => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.setTimeout(releaseInitialAutoplay, lowPowerDevice ? 160 : 80);
      });
    });
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleInitialAutoplay, { once: true });
  } else {
    scheduleInitialAutoplay();
  }

  // Safari pauzeert media geregeld na tabwissel, terugnavigatie en rotatie.
  // Hervat dan alleen de video's die werkelijk in beeld staan.
  let resumeSequenceVideos = () => {};
  const resumeVisibleMedia = () => {
    autoplayVisibleVideos();
    resumeSequenceVideos();
  };
  // content-visibility kan bij een grote mobiele scroll incidenteel één
  // IntersectionObserver-overgang overslaan. Controleer daarom één keer nadat
  // de scroll tot rust komt; geen continue layoutmetingen tijdens het scrollen.
  let mediaScrollSyncTimer = 0;
  const scheduleMediaScrollSync = () => {
    window.clearTimeout(mediaScrollSyncTimer);
    mediaScrollSyncTimer = window.setTimeout(() => {
      syncNonSequenceVideos(true);
      resumeSequenceVideos(true);
    }, 120);
  };
  // Deze extra scroll-check is alleen nodig voor Safari's sporadisch gemiste
  // content-visibility/IntersectionObserver-overgang. Chromium/Edge krijgt zo
  // geen serie layoutmetingen direct na iedere scrollbeweging.
  const safariBrowser = /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(navigator.userAgent);
  if (safariBrowser || !("IntersectionObserver" in window)) {
    window.addEventListener("scroll", scheduleMediaScrollSync, { passive: true });
  }
  let mediaPrimeInFlight = false;
  const detachMediaPrime = () => {
    document.removeEventListener("touchstart", primeMediaSession, true);
  };
  const primeMediaSession = () => {
    if (mediaSessionUnlocked || mediaPrimeInFlight) return;
    const video = allVideos.find((candidate) => elementVisibleRatio(candidate) > 0) || allVideos[0];
    if (!video) return;

    mediaPrimeInFlight = true;
    video.dataset.gesturePriming = "1";
    video.dataset.gesturePrimedAt = String(Date.now());
    video.dataset.playRequest = String(++playRequestId);
    video.dataset.playWanted = "1";
    video.dataset.autoplayState = "gesture-priming";
    video.playsInline = true;
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
    video.setAttribute("muted", "");
    video.preload = "auto";
    ensureVideo(video);

    let finished = false;
    const finishPrime = () => {
      if (finished) return;
      finished = true;
      mediaSessionUnlocked = true;
      mediaPrimeInFlight = false;
      delete video.dataset.gesturePriming;
      detachMediaPrime();
      if (!videoMayPlay(video)) stopVideo(video, "primed-out-of-view");
      else video.dataset.autoplayState = "playing";
      window.setTimeout(resumeVisibleMedia, 0);
    };
    const failPrime = () => {
      video.removeEventListener("playing", finishPrime);
      mediaPrimeInFlight = false;
      delete video.dataset.gesturePriming;
      video.dataset.autoplayState = "waiting";
    };

    video.addEventListener("playing", finishPrime, { once: true });
    const attempt = video.play();
    if (attempt && typeof attempt.then === "function") attempt.then(finishPrime).catch(failPrime);
    window.setTimeout(() => {
      if (!finished && video.dataset.gesturePriming === "1" && !video.paused) finishPrime();
    }, 180);
  };
  document.addEventListener("touchstart", primeMediaSession, { capture: true, passive: true });
  window.addEventListener("pageshow", resumeVisibleMedia);
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      allVideos.forEach((video) => stopVideo(video, "paused-hidden-tab"));
    } else {
      resumeVisibleMedia();
    }
  });
  window.addEventListener("pagehide", () => {
    allVideos.forEach((video) => stopVideo(video, "paused-pagehide"));
  });
  window.addEventListener("orientationchange", () => window.setTimeout(resumeVisibleMedia, 180));

  /* ---------- stappenvideo's: automatisch ná elkaar ---------- */
  const seq = $("[data-sequence]");
  if (seq) {
    const seqCards = $$("[data-video-card]", seq);
    const seqVids = seqCards.map((c) => $("video", c));
    // Een eigen posterlaag houdt exact de beginfoto zichtbaar terwijl de drie
    // kleine videodecoders warm blijven. Daardoor is video.load() bij iedere
    // stapwissel niet meer nodig en verdwijnt de periodieke Edge-hik.
    seqCards.forEach((card, index) => {
      const video = seqVids[index];
      const posterUrl = video && (video.dataset.posterUrl || video.dataset.poster || video.poster);
      if (!video || !posterUrl || $(".step__poster", card)) return;
      const poster = document.createElement("img");
      poster.className = "step__poster";
      poster.alt = "";
      poster.setAttribute("aria-hidden", "true");
      poster.loading = "lazy";
      poster.decoding = "async";
      poster.width = 720;
      poster.height = 540;
      poster.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";
      poster.dataset.src = posterUrl;
      video.insertAdjacentElement("afterend", poster);
      if (mediaObserver) mediaObserver.observe(poster);
      else loadDeferredImage(poster);
    });
    let seqIdx = 0;
    let seqActive = false;
    let seqPrepareTimer = 0;
    let seqSwipeTimer = 0;
    const primeSequenceVideo = (video, preload = "metadata") => {
      if (!video) return;
      // iOS beoordeelt autoplay wanneer de bron voor het eerst wordt gekoppeld.
      // Zet dus ook bij prewarming alle media-eigenschappen vooraf expliciet.
      video.playsInline = true;
      video.setAttribute("playsinline", "");
      video.setAttribute("webkit-playsinline", "");
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute("muted", "");
      video.preload = preload;
      ensureVideo(video);
    };
    const seqInView = (fresh = false) => {
      const cachedRatio = fresh ? undefined : visibilityRatios.get(seq);
      return (typeof cachedRatio === "number" ? cachedRatio : elementVisibleRatio(seq)) >= 0.5;
    };
    const seqMark = (i) => {
      const changed = seqIdx !== i || !seqCards[i].classList.contains("is-current");
      seqIdx = i;
      seqCards.forEach((c, j) => c.classList.toggle("is-current", j === i));
      // Mobiel: centreer de spelende kaart, zodat de hele video zichtbaar is.
      if (changed && seq.scrollWidth > seq.clientWidth + 8) {
        const cardRect = seqCards[i].getBoundingClientRect();
        const seqRect = seq.getBoundingClientRect();
        const target = seq.scrollLeft + (cardRect.left - seqRect.left)
          - ((seq.clientWidth - cardRect.width) / 2);
        const left = clamp(target, 0, Math.max(0, seq.scrollWidth - seq.clientWidth));
        try {
          seq.scrollTo({ left, behavior: reduceMotion ? "auto" : "smooth" });
        } catch (_) {
          seq.scrollLeft = left;
        }
      }
    };
    const resetSequenceVideo = (video, state = "paused-sequence") => {
      stopVideo(video, state);
      const card = video.closest("[data-video-card]");
      if (card) card.classList.remove("is-video-ready");
      if (video.dataset.mediaReady !== "1") return;
      if (video.currentTime > 0.01) {
        try { video.currentTime = 0; } catch (_) { /* metadata nog niet klaar */ }
      }
    };
    const seqPlay = (i, restart) => {
      seqMark(i);
      seqVids.forEach((v, j) => { if (j !== i) resetSequenceVideo(v); });
      const v = seqVids[i];
      if ((restart || v.ended) && v.dataset.mediaReady === "1" && v.currentTime > 0.01) {
        try { v.currentTime = 0; } catch (_) { /* metadata nog niet klaar */ }
      }
      // safePlay zet muted/defaultMuted vóór ensureVideo(). Juist dit verschil
      // ten opzichte van de overige video's blokkeerde de sequence op iPhone.
      safePlay(v, { forceMuted: true });
    };
    const prepareNextVideo = (i) => {
      window.clearTimeout(seqPrepareTimer);
      seqPrepareTimer = window.setTimeout(() => {
        if (!seqActive || !seqInView()) return;
        const next = seqVids[(i + 1) % seqVids.length];
        if (next.dataset.mediaReady === "1") return;
        primeSequenceVideo(next, "metadata");
      }, mobileLite ? 450 : 250);
    };

    // Start het laden van stap 1 pas wanneer de sectie nadert, maar ruim vóór
    // de vereiste 50%-autoplaygrens. Zo hoeft iPhone niet eerst een bron te
    // openen op exact het moment dat de bezoeker de kaart in beeld scrolt.
    if ("IntersectionObserver" in window) {
      const sequencePrewarmObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        primeSequenceVideo(seqVids[seqIdx], "auto");
        sequencePrewarmObserver.disconnect();
      }, { threshold: 0, rootMargin: mobileLite ? "280px 0px" : "520px 0px" });
      sequencePrewarmObserver.observe(seq);
    } else {
      primeSequenceVideo(seqVids[seqIdx], "auto");
    }
    seqVids.forEach((v, i) => {
      v.addEventListener("ended", () => {
        if (seqInView()) seqPlay((i + 1) % seqVids.length, true);
        else resetSequenceVideo(v, "paused-out-of-view");
      });
      v.addEventListener("play", () => {
        if (v.dataset.playWanted === "1") {
          seqVids.forEach((other, j) => {
            if (j !== i && (!other.paused || other.dataset.playWanted === "1")) {
              resetSequenceVideo(other);
            }
          });
          seqMark(i);
        }
      });
      v.addEventListener("playing", () => {
        if (v.dataset.playWanted === "1") prepareNextVideo(i);
      });
    });
    seq.addEventListener("nb:sequence-select", (event) => {
      const index = Number(event.detail && event.detail.index);
      if (!Number.isInteger(index) || index < 0 || index >= seqVids.length) return;
      seqPlay(index, true);
    });
    seq.addEventListener("nb:sequence-poster", (event) => {
      const index = Number(event.detail && event.detail.index);
      if (!Number.isInteger(index) || index < 0 || index >= seqVids.length) return;
      resetSequenceVideo(seqVids[index], "paused-by-user");
    });

    // Een afgeronde touch-swipe geldt als een bewuste videokeuze. Kies alleen
    // een volledig zichtbare kaart en, op bredere tablets, degene die het
    // dichtst bij het midden staat.
    const selectSettledSwipeCard = () => {
      window.clearTimeout(seqSwipeTimer);
      seqSwipeTimer = 0;
      if (!seqActive || document.hidden || seq.scrollWidth <= seq.clientWidth + 8) return;
      const railRect = seq.getBoundingClientRect();
      const railCenter = railRect.left + (railRect.width / 2);
      const visibleCards = seqCards.map((card, index) => {
        const rect = card.getBoundingClientRect();
        const fullyVisible = rect.left >= railRect.left - 2 && rect.right <= railRect.right + 2;
        return fullyVisible
          ? { index, distance: Math.abs((rect.left + (rect.width / 2)) - railCenter) }
          : null;
      }).filter(Boolean).sort((a, b) => a.distance - b.distance);
      if (visibleCards.length && visibleCards[0].index !== seqIdx) {
        seqPlay(visibleCards[0].index, true);
      }
    };
    const scheduleSwipeSelection = () => {
      window.clearTimeout(seqSwipeTimer);
      seqSwipeTimer = window.setTimeout(selectSettledSwipeCard, 180);
    };
    if ("onscrollend" in seq) {
      seq.addEventListener("scrollend", selectSettledSwipeCard, { passive: true });
    } else {
      seq.addEventListener("scroll", scheduleSwipeSelection, { passive: true });
    }

    const syncSequenceVisibility = (fresh = false) => {
      seqActive = seqInView(fresh) && !document.hidden;
      if (seqActive) {
        if (seqVids[seqIdx].paused) seqPlay(seqIdx, false);
      } else {
        window.clearTimeout(seqPrepareTimer);
        seqVids.forEach((v, index) => {
          stopVideo(v, "paused-out-of-view");
          seqCards[index].classList.remove("is-video-ready");
        });
      }
    };
    resumeSequenceVideos = syncSequenceVisibility;
    if ("IntersectionObserver" in window) {
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => visibilityRatios.set(seq, entry.intersectionRatio));
        syncSequenceVisibility();
      }, { threshold: [0, 0.5], rootMargin: "0px" }).observe(seq);
    } else {
      let seqFallbackFrame = 0;
      const scheduleSequenceSync = () => {
        if (seqFallbackFrame) return;
        seqFallbackFrame = window.requestAnimationFrame(() => {
          seqFallbackFrame = 0;
          syncSequenceVisibility();
        });
      };
      window.addEventListener("scroll", scheduleSequenceSync, { passive: true });
      window.addEventListener("resize", scheduleSequenceSync);
      window.setTimeout(scheduleSequenceSync, 0);
    }
  }

  /* ---------- train overal: tabs ---------- */
  const places = nbAssetConfig.places || {};
  const placeBtns = $$("[data-place]");
  const placeStage = $(".places__stage");
  if (placeBtns.length && placeStage) {
    const img = $("[data-place-img]", placeStage);
    const idxEl = $("[data-place-index]", placeStage);
    const titleEl = $("[data-place-title]", placeStage);
    const copyEl = $("[data-place-copy]", placeStage);
    let placeRequest = 0;
    const selectPlace = (btn) => {
      const data = places[btn.dataset.place];
      if (!data) return;
      placeBtns.forEach((b) => {
        b.classList.toggle("is-active", b === btn);
        b.setAttribute("aria-selected", String(b === btn));
      });
      placeStage.classList.add("is-swapping");
      const request = ++placeRequest;
      const done = () => {
        if (request !== placeRequest) return;
        idxEl.textContent = data.index;
        titleEl.textContent = data.title;
        copyEl.textContent = data.copy;
        placeStage.classList.remove("is-swapping");
      };
      preloadDecodedImage(data.img).then((ok) => {
        if (request !== placeRequest) return;
        if (ok) {
          img.src = data.img;
          img.alt = data.alt;
        }
        done();
      });
    };

    let placeTimer = null;
    let placeAuto = true;
    let placeInView = false;
    const syncPlaceTimer = () => {
      const shouldRun = placeAuto && placeInView && !document.hidden;
      if (shouldRun && !placeTimer) {
        placeTimer = window.setInterval(() => {
          const cur = placeBtns.findIndex((b) => b.classList.contains("is-active"));
          selectPlace(placeBtns[(cur + 1) % placeBtns.length]);
        }, 4600);
      } else if (!shouldRun && placeTimer) {
        window.clearInterval(placeTimer);
        placeTimer = null;
      }
    };
    const restartPlaceAuto = () => {
      placeAuto = true;
      if (placeTimer) {
        window.clearInterval(placeTimer);
        placeTimer = null;
      }
      syncPlaceTimer();
    };
    placeBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        selectPlace(btn);
        restartPlaceAuto();
      });
    });
    new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          placeInView = e.intersectionRatio >= 0.35;
          syncPlaceTimer();
        });
      },
      { threshold: 0.35 }
    ).observe(placeStage);
    document.addEventListener("visibilitychange", syncPlaceTimer);
  }


  /* ---------- vergelijking (mobiel): duel-toggle ---------- */
  const duel = $("[data-duel]");
  if (duel) {
    const initialBenchOption = $("[data-duel-opt='bench']", duel);
    const initialPartnerOption = $("[data-duel-opt='partner']", duel);
    const initialFoot = $("[data-duel-foot]", duel);
    const duelCopy = nbAssetConfig.duel || {
      benchLabel: initialBenchOption ? initialBenchOption.textContent.trim() : "",
      partnerLabel: initialPartnerOption ? initialPartnerOption.textContent.trim() : "",
      benchFoot: initialFoot ? initialFoot.textContent.trim() : "",
      partnerFoot: initialPartnerOption ? initialPartnerOption.textContent.trim() : "",
    };
    const duelData = {
      bench: {
        foot: duelCopy.benchFoot,
        vals: ["yes", "no", "no", "mid", "yes"],
      },
      partner: {
        foot: duelCopy.partnerFoot,
        vals: ["no", "dash", "mid", "mid", "no"],
      },
    };
    const glyph = { yes: "✓", no: "✗", mid: "±", dash: "—" };
    const cells = $$("[data-duel-cell]", duel);
    const footEl = $("[data-duel-foot]", duel);
    const colLabel = $("[data-duel-collabel]", duel);
    const opts = $$("[data-duel-opt]", duel);
    const duelMotionQuery = window.matchMedia("(max-width: 700px)");
    const setDuel = (key) => {
      const d = duelData[key];
      if (!d) return;
      opts.forEach((o) => {
        const on = o.dataset.duelOpt === key;
        o.classList.toggle("is-active", on);
        o.setAttribute("aria-selected", String(on));
      });
      cells.forEach((c, i) => {
        c.textContent = glyph[d.vals[i]];
        c.className = "duel__val " + d.vals[i];
      });
      footEl.textContent = d.foot;
      if (colLabel) colLabel.textContent = key === "bench" ? duelCopy.benchLabel : duelCopy.partnerLabel;
      // De wisselanimatie bestaat alleen mobiel. Voorkom op desktop de anders
      // nutteloze volledige synchrone layout-read.
      if (duelMotionQuery.matches) {
        duel.classList.remove("is-switch");
        void duel.offsetWidth;
        duel.classList.add("is-switch");
      }
    };
    let duelTimer = null;
    let duelInView = false;
    const syncDuelTimer = () => {
      const shouldRun = duelInView && !document.hidden;
      if (shouldRun && !duelTimer) {
        duelTimer = window.setInterval(() => {
          const cur = opts.findIndex((x) => x.classList.contains("is-active"));
          setDuel(opts[(cur + 1) % opts.length].dataset.duelOpt);
        }, 5000);
      } else if (!shouldRun && duelTimer) {
        window.clearInterval(duelTimer);
        duelTimer = null;
      }
    };
    const restartDuelTimer = () => {
      if (duelTimer) {
        window.clearInterval(duelTimer);
        duelTimer = null;
      }
      syncDuelTimer();
    };
    opts.forEach((o) => o.addEventListener("click", () => {
      setDuel(o.dataset.duelOpt);
      // Een handmatige keuze begint een verse cyclus bij nul; daarna blijft de
      // automatische Bench/Partner-wissel gewoon actief.
      restartDuelTimer();
    }));
    setDuel("bench");
    new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          duelInView = e.intersectionRatio >= 0.4;
          syncDuelTimer();
        });
      },
      { threshold: 0.4 }
    ).observe(duel);
    document.addEventListener("visibilitychange", syncDuelTimer);
  }

  /* ---------- hotspots (touch) ---------- */
  $$("[data-hotspot]").forEach((h) => {
    h.addEventListener("click", () => {
      const open = h.getAttribute("aria-expanded") === "true";
      $$("[data-hotspot]").forEach((x) => x.setAttribute("aria-expanded", "false"));
      h.setAttribute("aria-expanded", String(!open));
    });
  });

  /* ---------- marquees (reviews + filmstrip): naadloze loop ---------- */
  const initMarqueeRow = (row) => {
    if (row.dataset.marqueeReady === "1") return;
    const set = row.firstElementChild;
    if (!set) return;
    const clone = set.cloneNode(true);
    clone.setAttribute("aria-hidden", "true");
    row.appendChild(clone);
    row.dataset.marqueeReady = "1";
    // De clone ontstaat nadat de globale lazy-media observer is gevuld.
    // Observeer zijn afbeeldingen ook, anders bereikt de loop uiteindelijk
    // nog niet geladen placeholders.
    if (mediaObserver) {
      $$("img[data-src]", clone).forEach((img) => mediaObserver.observe(img));
    } else {
      $$("img[data-src]", clone).forEach(loadDeferredImage);
    }
  };
  const marqueeRows = $$("[data-marquee-row]");
  if ("IntersectionObserver" in window) {
    const marqueeObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        initMarqueeRow(entry.target);
        marqueeObserver.unobserve(entry.target);
      });
    }, { threshold: 0, rootMargin: mobileLite ? "260px 0px" : "800px 0px" });
    marqueeRows.forEach((row) => marqueeObserver.observe(row));
  } else {
    marqueeRows.forEach(initMarqueeRow);
  }

  /* ---------- horizontale rails: touch-swipe + desktop grab ---------- */
  const suppressDraggedClick = (rail) => {
    rail.addEventListener("click", (event) => {
      if (rail.dataset.dragged !== "1") return;
      event.preventDefault();
      event.stopPropagation();
      rail.dataset.dragged = "0";
    }, true);
  };

  const initAnimatedDragRail = (rail) => {
    const getTracks = () => $$(".logos__track, .belt__track, .reviews__set, .wild__set", rail)
      .filter((track) => track.parentElement === rail);
    const initialTrack = getTracks()[0];
    if (!initialTrack || typeof initialTrack.getAnimations !== "function") return;

    rail.dataset.dragRail = "animated";
    rail.addEventListener("dragstart", (event) => event.preventDefault());
    suppressDraggedClick(rail);
    let gesture = null;

    const applyAnimatedDrag = (state) => {
      if (!state || !state.dragged) return;
      state.animations.forEach((animationState) => {
        const offset = (state.lastDx / animationState.travel) * animationState.duration * animationState.direction;
        const rawTime = animationState.currentTime - offset;
        animationState.animation.currentTime = ((rawTime % animationState.duration) + animationState.duration) % animationState.duration;
      });
    };

    const finish = () => {
      if (!gesture) return;
      const state = gesture;
      gesture = null;
      if (state.frameId) window.cancelAnimationFrame(state.frameId);
      applyAnimatedDrag(state);
      rail.classList.remove("is-dragging");
      if (state.dragged) rail.dataset.dragged = "1";
      if (!rail.closest(".is-motion-paused")) {
        state.animations.forEach(({ animation }) => animation.play());
      }
    };

    rail.addEventListener("pointerdown", (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      const animations = getTracks().map((track) => {
        const animation = track.getAnimations()[0];
        if (!animation) return null;
        const timing = animation.effect && animation.effect.getComputedTiming();
        const duration = timing && Number(timing.duration);
        if (!Number.isFinite(duration) || duration <= 0) return null;
        const reverse = getComputedStyle(track).animationDirection.includes("reverse");
        const divisor = track.classList.contains("logos__track") ? 2
          : track.classList.contains("belt__track") ? 3 : 1;
        const travel = Math.max(1, track.scrollWidth / divisor);
        const currentTime = Number(animation.currentTime) || 0;
        animation.pause();
        return { animation, duration, travel, currentTime, direction: reverse ? -1 : 1 };
      }).filter(Boolean);
      if (!animations.length) return;

      gesture = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        dragged: false,
        lastDx: 0,
        frameId: 0,
        animations,
      };
      rail.dataset.dragged = "0";
      try { rail.setPointerCapture(event.pointerId); } catch (_) { /* Safari fallback */ }
    });

    rail.addEventListener("pointermove", (event) => {
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      const dx = event.clientX - gesture.startX;
      const dy = event.clientY - gesture.startY;
      if (!gesture.dragged) {
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 7) {
          finish();
          return;
        }
        if (Math.abs(dx) < 6) return;
        gesture.dragged = true;
        rail.classList.add("is-dragging");
      }
      event.preventDefault();
      const state = gesture;
      state.lastDx = dx;
      if (!state.frameId) {
        state.frameId = window.requestAnimationFrame(() => {
          state.frameId = 0;
          if (gesture === state) applyAnimatedDrag(state);
        });
      }
    }, { passive: false });
    rail.addEventListener("pointerup", finish);
    rail.addEventListener("pointercancel", finish);
    rail.addEventListener("lostpointercapture", finish);
  };

  const initNativeDragRail = (rail) => {
    rail.dataset.dragRail = "native";
    rail.addEventListener("dragstart", (event) => event.preventDefault());
    suppressDraggedClick(rail);
    let gesture = null;
    const applyNativeDrag = (state) => {
      if (state && state.dragged) rail.scrollLeft = state.startScroll - state.lastDx;
    };
    const finish = () => {
      if (!gesture) return;
      const state = gesture;
      gesture = null;
      if (state.frameId) window.cancelAnimationFrame(state.frameId);
      applyNativeDrag(state);
      if (state.dragged) rail.dataset.dragged = "1";
      rail.classList.remove("is-dragging");
    };
    rail.addEventListener("pointerdown", (event) => {
      // Touch en trackpad gebruiken de veel soepelere native overflow-scroll.
      // Alleen een echte desktopmuis krijgt de click-and-drag aanvulling.
      if (event.pointerType !== "mouse" || event.button !== 0) return;
      gesture = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startScroll: rail.scrollLeft,
        dragged: false,
        lastDx: 0,
        frameId: 0,
      };
      rail.dataset.dragged = "0";
    });
    rail.addEventListener("pointermove", (event) => {
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      const dx = event.clientX - gesture.startX;
      const dy = event.clientY - gesture.startY;
      if (!gesture.dragged) {
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 7) {
          finish();
          return;
        }
        if (Math.abs(dx) < 6) return;
        gesture.dragged = true;
        rail.classList.add("is-dragging");
        // Capture pas nadat een echte horizontale sleep is vastgesteld.
        // Als de rail al op pointerdown capturet, wordt de daaropvolgende
        // desktopklik naar de rail geretarget en bereikt die de videoknop niet.
        try { rail.setPointerCapture(event.pointerId); } catch (_) { /* Safari fallback */ }
      }
      event.preventDefault();
      const state = gesture;
      state.lastDx = dx;
      if (!state.frameId) {
        state.frameId = window.requestAnimationFrame(() => {
          state.frameId = 0;
          if (gesture === state) applyNativeDrag(state);
        });
      }
    }, { passive: false });
    rail.addEventListener("pointerup", finish);
    rail.addEventListener("pointercancel", finish);
    rail.addEventListener("lostpointercapture", finish);
  };

  $$(".logos__frame, .belt, [data-marquee-row]").forEach(initAnimatedDragRail);
  $$(".gallery__thumbs, .steps__grid").forEach(initNativeDragRail);

  /* ---------- productgalerij in de kit ---------- */
  const gallery = $("[data-gallery]");
  if (gallery) {
    const main = $("[data-gal-main]", gallery);
    const hotspots = $$("[data-gal-hotspot]", gallery);
    const countEl = $("[data-gal-count]", gallery);
    const thumbs = $$(".gallery__thumbs button");
    let galIdx = 0;
    let galleryRequest = 0;
    const showGal = (idx) => {
      if (idx === galIdx) return;
      const btn = thumbs[idx];
      galIdx = idx;
      thumbs.forEach((b, j) => b.classList.toggle("is-active", j === idx));
      gallery.classList.add("is-swapping");
      const request = ++galleryRequest;
      const apply = () => {
        if (request !== galleryRequest) return;
        // De responsive srcset hoort bij de eerste baanfoto. Na een bewuste
        // galerijkeuze moet de gekozen bron de volledige controle krijgen.
        main.removeAttribute("srcset");
        main.src = btn.dataset.gal;
        main.alt = btn.dataset.galAlt || "";
        if (countEl) countEl.textContent = String(idx + 1);
        // hotspots horen alleen bij de eerste (baan)foto
        hotspots.forEach((h) => { h.hidden = idx !== 0; });
        gallery.classList.remove("is-swapping");
      };
      preloadDecodedImage(btn.dataset.gal).then(apply);
    };
    thumbs.forEach((btn, idx) => btn.addEventListener("click", () => showGal(idx)));
    // klik op de grote foto = volgende
    main.addEventListener("click", () => showGal((galIdx + 1) % thumbs.length));
  }

  /* ---------- scroll-to knoppen ---------- */
  $$("[data-scroll-to]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const el = $(btn.dataset.scrollTo);
      if (el) el.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth" });
    });
  });

  /* ---------- sticky buybar (mobiel) + buy-dock (desktop) ---------- */
  const buybar = $("[data-buybar]");
  const dock = $("[data-dock]");
  const hero = $("[data-hero]");
  if (hero && (buybar || dock)) {
    let heroVis = true;
    let moveVis = false;
    let pageBuyCtaFullyVisible = false;
    let idleTimer = null;
    let idleDeadline = 0;
    const buybarIdleDelay = 2100;
    const applyDock = () => {
      if (dock) dock.hidden = heroVis || moveVis;
    };
    // Mobiele koopbalk: pas tonen wanneer de gebruiker even niets doet en er
    // geen volledige bestelknop in de hoofdcontent zichtbaar is.
    const hideBuybar = () => { if (buybar && !buybar.hidden) buybar.hidden = true; };
    const revealBuybarAfterIdle = () => {
      const remaining = idleDeadline - performance.now();
      if (remaining > 16) {
        idleTimer = window.setTimeout(revealBuybarAfterIdle, remaining);
        return;
      }
      idleTimer = null;
      if (!heroVis && !pageBuyCtaFullyVisible && !(cart && cart.open)) buybar.hidden = false;
    };
    const queueBuybar = () => {
      if (!buybar || !compactBuybarViewport) return;
      hideBuybar();
      idleDeadline = performance.now() + buybarIdleDelay;
      if (!idleTimer) idleTimer = window.setTimeout(revealBuybarAfterIdle, buybarIdleDelay);
    };
    if (compactBuybarViewport) {
      const pressEvent = "PointerEvent" in window ? "pointerdown" : "touchstart";
      ["scroll", pressEvent, "keydown"].forEach((ev) =>
        window.addEventListener(ev, queueBuybar, { passive: true })
      );

      const pageBuyCtas = $$("main [data-nb-quick-add]");
      if (pageBuyCtas.length) {
        const fullyVisibleCtas = new Set();
        const ctaObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const rootBottom = entry.rootBounds ? entry.rootBounds.bottom : window.innerHeight;
            const fullyVisible =
              entry.isIntersecting &&
              entry.intersectionRatio >= 0.999 &&
              entry.boundingClientRect.top >= 0 &&
              entry.boundingClientRect.bottom <= rootBottom;
            if (fullyVisible) fullyVisibleCtas.add(entry.target);
            else fullyVisibleCtas.delete(entry.target);
          });
          pageBuyCtaFullyVisible = fullyVisibleCtas.size > 0;
          if (pageBuyCtaFullyVisible) hideBuybar();
          else queueBuybar();
        }, { threshold: [0, 1] });
        pageBuyCtas.forEach((cta) => ctaObserver.observe(cta));
      }
    }
    new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { heroVis = e.isIntersecting; });
        applyDock();
        queueBuybar();
      },
      { threshold: 0.05 }
    ).observe(hero);
    if (move) {
      new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => { moveVis = e.isIntersecting; });
          applyDock();
        },
        { threshold: 0.01 }
      ).observe(move);
    }
    queueBuybar();
  }

  /* ---------- live cart is handled by nb-v10-site.js ---------- */
  const cart = $("[data-nb-cart-dialog]");

  /* ---------- jaar ---------- */
  const yearEl = $("[data-year]");
  if (yearEl) yearEl.textContent = String(new Date().getFullYear());
})();
